import { useUserEventLogger } from '@repo/tracking-service';
import { useAuthContext } from '@repo/user-session';
import { useRouter } from 'next/router';
import { useState, useEffect, useMemo, useCallback, ReactNode } from 'react';

import { useSectionScrollModel } from '@/components/common/context/SectionScrollModelProvider';
import { WaitingModal } from '@/components/landing/WaitingModal';
import { AUTH_PATHS, COMMON_PATHS } from '@/lib/constants/paths';
import { useRandomWaitingModal } from '@/lib/hooks/use-random-waiting-modal';
import { useUrlWithQueryString } from '@/lib/hooks/use-url-with-query-string';

interface CountDownCTAProps {
  endDate: string;
  title: ReactNode;
  backgroundClassName: string;
  buttonClassName: string;
}

// 시간 계산 유틸리티 함수
const calculateTimeLeft = (endDate: Date) => {
  const now = new Date();

  // 종료 시간이 지났으면 0으로 설정
  if (now > endDate) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0
    };
  }

  const difference = endDate.getTime() - now.getTime();

  const days = Math.floor(difference / (1000 * 60 * 60 * 24));
  const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((difference % (1000 * 60)) / 1000);

  return {
    days,
    hours,
    minutes,
    seconds
  };
};

// 시간 텍스트 컴포넌트
const TimeText = ({ value, label }: { value: number; label: string }) => (
  <p className={'flex items-center'}>
    <b className={'heading-small-bold leading-[142%] min-w-7 text-center'}>{String(value).padStart(2, '0')}</b>
    <span className={'caption-small-semibold leading-[147%] min-w-3 text-center'}>{label}</span>
  </p>
);

// 콜론 컴포넌트
const TimeColon = () => <b className={'heading-small-medium leading-[142%]'}>:</b>;

// 시간 표시 컴포넌트
const TimeDisplay = ({ value, label }: { value: number; label: string }) => (
  <>
    <TimeText value={value} label={label} />
    {label !== '초' && <TimeColon />}
  </>
);

export const CountDownCTA = ({ endDate, title, backgroundClassName, buttonClassName }: CountDownCTAProps) => {
  const router = useRouter();
  const { auth } = useAuthContext();
  const actionUrl = useUrlWithQueryString(auth.status === 'login' ? COMMON_PATHS.환급조회목록 : AUTH_PATHS.로그인);
  const { sendClickEvent } = useUserEventLogger();
  const { eventAttribute } = useSectionScrollModel();
  const { waitingTime, showWaitingModal, waitingCallback, setWaitingCallback } = useRandomWaitingModal();
  // 종료 시간을 useMemo로 메모이제이션
  const endDateObj = useMemo(() => new Date(endDate), [endDate]);

  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  const clickCTAButton = () => {
    sendClickEvent('landing_bottom_cta', eventAttribute);
    showWaitingModal();
  };

  // 시간 계산 함수를 useCallback으로 메모이제이션
  const updateTimeLeft = useCallback(() => {
    setTimeLeft(calculateTimeLeft(endDateObj));
  }, [endDateObj]);

  useEffect(() => {
    // 초기 계산
    updateTimeLeft();

    // 1초마다 업데이트
    const timer = setInterval(updateTimeLeft, 1000);

    // 컴포넌트 언마운트 시 타이머 정리
    return () => clearInterval(timer);
  }, [updateTimeLeft]);

  useEffect(() => {
    setWaitingCallback(() => () => {
      router.push(actionUrl);
    });
  }, [actionUrl, router, setWaitingCallback]);

  return (
    <div className={`${backgroundClassName} text-white sticky bottom-0 left-0 right-0 flex flex-col items-center w-full max-w-full`}>
      <div className={'py-2 px-5 flex flex-col items-center'}>
        {title}
        <div className={'flex items-center gap-x-tiny-small'}>
          <TimeDisplay value={timeLeft.days} label="일" />
          <TimeDisplay value={timeLeft.hours} label="시간" />
          <TimeDisplay value={timeLeft.minutes} label="분" />
          <TimeDisplay value={timeLeft.seconds} label="초" />
        </div>
      </div>

      <div className={'px-4.5 pb-5 w-full max-w-screen-basicMax text-center'}>
        <button
          type={'button'}
          className={`${buttonClassName} py-4 px-5 w-full max-w-full rounded-2xl text-17 font-medium leading-[24px]`}
          onClick={clickCTAButton}
        >
          <span>{'내 환급금 무료 조회하기$>;'}</span>
        </button>
      </div>
      {waitingTime > 0 && <WaitingModal waitingTime={waitingTime} callbackFunction={waitingCallback} />}
    </div>
  );
};
